import { CellType, useMappingStore } from "@/stores/mapping";
import { Skeleton } from "@/styles/ui/skeleton";
import { Role, TagType } from "@/types/mapping";
import { useMemo } from "react";
import { toast } from "sonner";
import { MappingSearchResultGroup } from "./group";
import { useMappingSearchResults } from "./hooks";
import { NoSearchResult } from "./no-search-result";
import { NonValueCell } from "./non-value-cell";
import { MappingSearchResultItem } from "./search-result-item";
import { MappingSearchResultList } from "./search-result-list";
import { MemberTagsViewType, MemberViewType } from "./types";
import { useTagClick } from "./use-tag-click";

const flattenAxisTag: (
  children: MemberViewType[],
  filter_function: (tag: MemberViewType) => boolean,
) => MemberViewType[] = (children, filter_function) => {
  const newChildren: MemberViewType[] = [];
  children.forEach((child: MemberViewType) => {
    newChildren.push(...flattenAxisTag(child.children, filter_function));
    if (filter_function(child)) {
      newChildren.push(child);
    }
  });
  return newChildren;
};

export const MappingSearchResultAxisSection = ({
  noResultActionComponent,
}: {
  noResultActionComponent: React.ReactNode;
}) => {
  const dimension = useMappingStore((state) => state.dimension.dimension);
  const {
    data: tags,
    isLoading,
    isError,
    refetch,
  } = useMappingSearchResults(dimension);
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const handleTagClick = useTagClick({
    toast: { onError: () => toast.error("태그 추가에 실패했습니다.") },
  });

  const searchedTags = useMemo(() => {
    const newTags = Array.from(tags.entries()).map(([_, axisList]) => {
      return {
        role: axisList.role,
        tags: axisList.tags.map((tag: MemberTagsViewType) => {
          return {
            ...tag,
            children: flattenAxisTag(tag.children, (tag) => !tag.isAddedTag),
          };
        }),
      };
    });
    return newTags;
  }, [tags]);
  const addedTags = useMemo(() => {
    const newTags = Array.from(tags.entries()).map(([_, axisList]) => {
      return {
        role: axisList.role,
        tags: axisList.tags.map((tag: MemberTagsViewType) => {
          return {
            ...tag,
            children: flattenAxisTag(tag.children, (tag) => !!tag.isAddedTag),
          };
        }),
      };
    });
    return newTags;
  }, [tags]);

  if (isLoading) {
    return (
      <div className="flex pb-[12px] flex-col items-start self-stretch">
        <div className="flex py-[12px] px-[16px] h-[48px] items-center gap-[10px] self-stretch border-t-[1px] border-neutral-200">
          <Skeleton className="h-[14px] w-[140px] rounded-md bg-neutral-100" />
        </div>
        <div className="flex py-0 px-[16px] flex-col items-start gap-[20px] self-stretch">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-[36px] w-full rounded-[8px] bg-neutral-100"
            />
          ))}
        </div>
      </div>
    );
  }

  const hasAnySearchResult = searchedTags
    .concat(addedTags)
    .reduce((acc, tagList) => {
      return acc || (tagList.role !== null && tagList.tags.length > 0);
    }, false);

  const render = () => {
    if (selectedFact?.cellType) {
      if (CellType.NONE == selectedFact?.cellType) {
        return <NonValueCell text="값이 없습니다." />;
      } else if (CellType.HEADER == selectedFact?.cellType) {
        return <NonValueCell text="헤더 영역입니다." />;
      }
    }
    if (isError) {
      return (
        <NoSearchResult
          text="앗, 태그를 불러오지 못했습니다."
          refetch={async () => {
            await refetch();
          }}
        />
      );
    } else if (searchedTags.length + addedTags.length > 0) {
      return (
        <>
          <MemberTagsSection
            tags={addedTags}
            label="직접 추가한 구성요소"
            onTagClick={handleTagClick}
          />
          <MemberTagsSection
            tags={searchedTags}
            label="추천 구성요소"
            onTagClick={handleTagClick}
          />
          {noResultActionComponent}
        </>
      );
    } else if (!hasAnySearchResult || !selectedFact) {
      return (
        <div className="w-full h-full flex flex-col pb-4">
          <NoSearchResult
            text="검색 결과가 없습니다."
            refetch={async () => {
              await refetch();
            }}
            showRefreshButton={false}
          />
          {noResultActionComponent}
        </div>
      );
    }
  };

  return render();
};

const MemberTagsSection = ({
  tags,
  label,
  onTagClick,
}: {
  tags: {
    role: Role | null;
    tags: {
      children: MemberViewType[];
      id: string;
      prefix: string;
      description: string | null;
      name: string;
      label: string;
      type: TagType;
      inRole: boolean;
      multiSelect: boolean;
      role: Role | null;
    }[];
  }[];
  label: string;
  onTagClick: (tag: MemberViewType) => void;
}) => {
  const tagKeysCount = useMemo(
    () =>
      tags.reduce((acc, tagList) => {
        return (
          acc +
          tagList.tags.reduce((acc, tag) => {
            return acc + tag.children.length;
          }, 0)
        );
      }, 0),
    [tags],
  );

  if (tagKeysCount === 0) {
    return <></>;
  }

  return (
    <>
      {tags.map(
        (tagList) =>
          tagList.role?.label &&
          tagList.tags.length > 0 && (
            <MappingSearchResultList
              label={label}
              key={tagList.role?.label ?? "OTHERS"}
            >
              {tagList.tags
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(
                  (tag: MemberTagsViewType) =>
                    tag.children.length > 0 && (
                      <MappingSearchResultGroup
                        label={tag.label}
                        key={tag.label}
                      >
                        {tag.children
                          .sort((a, b) => a.label.localeCompare(b.label))
                          .map((child: MemberViewType) => (
                            <MappingSearchResultItem
                              label={child.label}
                              tag={child}
                              onClick={onTagClick}
                              key={child.id}
                            />
                          ))}
                      </MappingSearchResultGroup>
                    ),
                )}
            </MappingSearchResultList>
          ),
      )}
    </>
  );
};
